import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { API, getAuthHeader } from '../utils/api';
import { approximateJsonBytes, bytesToHuman, prepareUpload } from '../utils/worksheetMarkerImageUtils';
import { downloadMarkingPdf } from '../utils/worksheetMarkerPdf';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Upload, Trash2, FileDown, Loader2, RefreshCw, Sparkles, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const WORKSHEET_TYPES = [
  'Auto-detect', 'Grammar MCQ', 'Vocabulary MCQ', 'Grammar Cloze', 'Vocabulary Cloze',
  'Comprehension Cloze', 'Editing', 'Synthesis and Transformation', 'Comprehension',
  'Composition', 'Mixed worksheet',
];

const LEVELS = [
  'Primary 1', 'Primary 2', 'Primary 3', 'Primary 4', 'Primary 5', 'Primary 6',
  'Secondary 1', 'Secondary 2', 'Secondary 3', 'Secondary 4',
];

const STATUS_STYLES = {
  correct:    'bg-emerald-100 text-emerald-800 border-emerald-200',
  incorrect:  'bg-rose-100 text-rose-800 border-rose-200',
  partial:    'bg-amber-100 text-amber-800 border-amber-200',
  review:     'bg-sky-100 text-sky-800 border-sky-200',
  unanswered: 'bg-slate-200 text-slate-700 border-slate-300',
};

const STATUS_LABEL = {
  correct: 'Correct',
  incorrect: 'Incorrect',
  partial: 'Partially correct',
  review: 'Tutor review',
  unanswered: 'Unanswered',
};

const MAX_UPLOAD_BYTES = 5_600_000;

const WorksheetMarkerPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [files, setFiles] = useState([]);
  const [studentName, setStudentName] = useState('');
  const [level, setLevel] = useState('Primary 5');
  const [worksheetType, setWorksheetType] = useState('Auto-detect');
  const [tutorNotes, setTutorNotes] = useState('');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [featureEnabled, setFeatureEnabled] = useState(true);
  const [checkingFeature, setCheckingFeature] = useState(true);

  // Check if feature is enabled
  useEffect(() => {
    const checkFeatureStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/worksheet-marker/status`);
        const data = await response.json();
        setFeatureEnabled(data.enabled);
      } catch (error) {
        console.error('Failed to check feature status:', error);
      } finally {
        setCheckingFeature(false);
      }
    };
    checkFeatureStatus();
  }, []);

  const totalSize = useMemo(() => files.reduce((sum, f) => sum + f.size, 0), [files]);

  const onFilesSelected = async (event) => {
    setError('');
    const selected = Array.from(event.target.files || []);
    event.target.value = '';
    if (!selected.length) return;
    if (files.length + selected.length > 6) {
      setError('Upload no more than 6 worksheet pages at a time.');
      return;
    }
    try {
      const prepared = [];
      for (const f of selected) prepared.push(await prepareUpload(f));
      setFiles((curr) => [...curr, ...prepared]);
    } catch (e) {
      setError(e.message || 'One of the files could not be prepared.');
    }
  };

  const removeFile = (id) => setFiles((curr) => curr.filter((f) => f.id !== id));

  const markWorksheet = async (event) => {
    event.preventDefault();
    setError('');
    setResult(null);

    if (!files.length) return setError('Scan or upload at least one worksheet page.');
    if (!consent) return setError('Confirm that you have permission to process the worksheet.');

    const payload = {
      studentName: studentName.trim(),
      level,
      worksheetType,
      tutorNotes: tutorNotes.trim(),
      files: files.map(({ name, mimeType, dataUrl }) => ({ name, mimeType, dataUrl })),
    };

    if (approximateJsonBytes(payload) > MAX_UPLOAD_BYTES) {
      return setError('The prepared upload is too large. Remove a page or use lower-resolution scans.');
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/worksheet-marker/mark`, payload, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        timeout: 120000,
      });
      setResult(data.result);
      toast.success('Draft marking ready — review before generating the PDF.');
    } catch (e) {
      const detail = e.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : (e.message || 'Unable to mark the worksheet.');
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const updateQuestion = (index, field, value) => {
    setResult((curr) => {
      const questions = curr.questions.map((q, i) => (i === index ? { ...q, [field]: value } : q));
      const totalScore = questions.reduce((s, q) => s + Number(q.marksAwarded || 0), 0);
      const totalPossible = questions.reduce((s, q) => s + Number(q.marksPossible || 0), 0);
      return {
        ...curr,
        questions,
        totalScore,
        totalPossible,
        percentage: totalPossible > 0 ? (totalScore / totalPossible) * 100 : 0,
      };
    });
  };

  const updateResult = (field, value) => setResult((curr) => ({ ...curr, [field]: value }));

  const reset = () => {
    setFiles([]);
    setResult(null);
    setTutorNotes('');
    setError('');
  };

  if (user && user.role !== 'tutor' && user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className="p-8 max-w-md text-center rounded-2xl">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Tutors only</h2>
          <p className="text-sm text-slate-600 mb-4">
            The AI Worksheet Marker is a Tutor tool. Sign in with a tutor account to access it.
          </p>
          <Button onClick={() => navigate('/tutor/register')} data-testid="wm-goto-tutor-register">
            Create a Tutor account
          </Button>
        </Card>
      </div>
    );
  }

  // Show disabled state if feature is turned off
  if (checkingFeature) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (!featureEnabled) {
    return (
      <div className="min-h-screen bg-slate-50 pb-16">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <button
              onClick={() => navigate('/tutor/dashboard')}
              className="inline-flex items-center gap-2 text-slate-700 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Tutor Dashboard
            </button>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 pt-16 text-center">
          <Card className="p-8 rounded-3xl border-2 border-amber-200">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Feature Temporarily Disabled</h1>
            <p className="text-slate-600 mb-6">
              The AI English Worksheet Marker has been disabled by the administrator. Please check back later or contact support for more information.
            </p>
            <Button
              onClick={() => navigate('/tutor/dashboard')}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              Back to Dashboard
            </Button>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/tutor/dashboard')}
            className="inline-flex items-center gap-2 text-slate-700 hover:text-slate-900"
            data-testid="wm-back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Tutor Dashboard
          </button>
          <div className="flex items-center gap-2 text-amber-700">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-semibold">AI Worksheet Marker</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 pt-8 space-y-6">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold text-slate-900" data-testid="wm-page-title">
            AI English Worksheet Marking Agent
          </h1>
          <p className="text-slate-600">
            Scan a student's worksheet, let Prof.Scaly read handwritten answers, review the draft marking, and download a printable PDF report.
          </p>
        </div>

        <Card className="p-4 rounded-2xl border-2 border-amber-200 bg-amber-50/70">
          <p className="text-sm text-amber-900">
            <strong>Student privacy:</strong> Use initials or a pseudonym. Cover names, school details and other identifiers before scanning. The tutor must review every AI-generated mark.
          </p>
        </Card>

        {!result ? (
          <Card className="p-6 rounded-2xl space-y-5" data-testid="wm-upload-panel">
            <form onSubmit={markWorksheet} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="wm-student">Student initials or pseudonym</Label>
                  <Input
                    id="wm-student"
                    value={studentName}
                    onChange={(e) => setStudentName(e.target.value)}
                    placeholder="e.g. Z.J."
                    maxLength={60}
                    data-testid="wm-input-student"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Level</Label>
                  <Select value={level} onValueChange={setLevel}>
                    <SelectTrigger data-testid="wm-select-level"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Worksheet type</Label>
                  <Select value={worksheetType} onValueChange={setWorksheetType}>
                    <SelectTrigger data-testid="wm-select-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {WORKSHEET_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="wm-notes">Tutor instructions or answer-key notes (optional)</Label>
                <Textarea
                  id="wm-notes"
                  value={tutorNotes}
                  onChange={(e) => setTutorNotes(e.target.value)}
                  placeholder="e.g. Award 1 mark per question. Ignore the teacher's ticks and crosses already written on the page."
                  rows={3}
                  maxLength={1500}
                  data-testid="wm-input-notes"
                />
              </div>

              <div className="rounded-xl border-2 border-dashed border-slate-300 p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <div className="font-semibold text-slate-900">1. Scan or upload worksheet pages</div>
                  <p className="text-sm text-slate-600">JPG or PNG only. Clear, upright photographs work best. Images are compressed before upload.</p>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-2 rounded-md">
                  <Upload className="w-4 h-4" />
                  <span>Choose files</span>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    multiple
                    onChange={onFilesSelected}
                    className="hidden"
                    data-testid="wm-file-input"
                  />
                </label>
              </div>

              {files.length > 0 && (
                <div className="space-y-2" data-testid="wm-file-list">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {files.map((f, i) => (
                      <div key={f.id} className="border rounded-lg overflow-hidden bg-white flex flex-col">
                        <img src={f.previewUrl} alt={`Page ${i + 1}`} className="w-full h-32 object-cover" />
                        <div className="p-2 text-xs space-y-1">
                          <div className="font-semibold text-slate-800">Page {i + 1}</div>
                          <div className="text-slate-500 truncate" title={f.name}>{f.name}</div>
                          <div className="text-slate-500">{bytesToHuman(f.size)}</div>
                          <button
                            type="button"
                            onClick={() => removeFile(f.id)}
                            className="inline-flex items-center gap-1 text-rose-600 hover:text-rose-700 text-xs"
                            data-testid={`wm-remove-file-${i}`}
                          >
                            <Trash2 className="w-3 h-3" /> Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500">Prepared upload size: {bytesToHuman(totalSize)}</p>
                </div>
              )}

              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                <Checkbox
                  checked={consent}
                  onCheckedChange={(v) => setConsent(Boolean(v))}
                  data-testid="wm-consent-checkbox"
                />
                <span>I have permission to process this worksheet and have removed unnecessary personal information.</span>
              </label>

              {error && (
                <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" role="alert" data-testid="wm-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700"
                data-testid="wm-submit-btn"
              >
                {loading
                  ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reading handwriting and marking…</>)
                  : (<>2. Mark worksheet</>)}
              </Button>
              {loading && (
                <p className="text-xs text-slate-500">
                  The agent is reading printed questions, separating handwritten answers from existing teacher marks and checking the English rules.
                </p>
              )}
            </form>
          </Card>
        ) : (
          <Card className="p-6 rounded-2xl space-y-5" data-testid="wm-result-panel">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Marking draft</div>
                <h2 className="text-2xl font-bold text-slate-900" data-testid="wm-result-title">{result.worksheetTitle}</h2>
                <p className="text-sm text-slate-600">
                  {result.worksheetType} · Scan quality: {result.scanQuality}
                </p>
              </div>
              <div className="text-right bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                <div className="text-2xl font-bold text-emerald-800" data-testid="wm-result-score">
                  {result.totalScore}/{result.totalPossible}
                </div>
                <div className="text-xs text-emerald-700">{Math.round(result.percentage)}%</div>
              </div>
            </div>

            {result.reviewRequired && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                Tutor review is required — one or more answers are unclear or low-confidence.
              </div>
            )}

            <div className="space-y-4">
              {result.questions.map((q, index) => (
                <Card key={`${q.number}-${index}`} className="p-4 rounded-xl border-l-4" style={{ borderLeftColor: q.status === 'correct' ? '#059669' : q.status === 'incorrect' ? '#e11d48' : '#f59e0b' }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold text-slate-900">Question {q.number}</div>
                    <Badge className={STATUS_STYLES[q.status] || STATUS_STYLES.review}>
                      {STATUS_LABEL[q.status] || q.status}
                    </Badge>
                  </div>
                  {q.questionText && <p className="text-sm text-slate-700 mb-3">{q.questionText}</p>}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Student's answer</Label>
                      <Textarea rows={2} value={q.studentAnswer} onChange={(e) => updateQuestion(index, 'studentAnswer', e.target.value)} data-testid={`wm-q-${index}-student`} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Correct answer</Label>
                      <Textarea rows={2} value={q.correctAnswer} onChange={(e) => updateQuestion(index, 'correctAnswer', e.target.value)} data-testid={`wm-q-${index}-correct`} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Result</Label>
                      <Select value={q.status} onValueChange={(v) => updateQuestion(index, 'status', v)}>
                        <SelectTrigger data-testid={`wm-q-${index}-status`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="correct">Correct</SelectItem>
                          <SelectItem value="incorrect">Incorrect</SelectItem>
                          <SelectItem value="partial">Partially correct</SelectItem>
                          <SelectItem value="review">Tutor review</SelectItem>
                          <SelectItem value="unanswered">Unanswered</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Marks awarded (out of {q.marksPossible})</Label>
                      <Input
                        type="number" min={0} step={0.5} max={q.marksPossible}
                        value={q.marksAwarded}
                        onChange={(e) => updateQuestion(index, 'marksAwarded', Number(e.target.value))}
                        data-testid={`wm-q-${index}-marks`}
                      />
                    </div>
                  </div>
                  <div className="space-y-1 mt-3">
                    <Label className="text-xs">Explanation</Label>
                    <Textarea rows={3} value={q.explanation} onChange={(e) => updateQuestion(index, 'explanation', e.target.value)} data-testid={`wm-q-${index}-explanation`} />
                  </div>
                  <p className="text-xs text-slate-500 mt-2">AI confidence: {q.confidence}</p>
                </Card>
              ))}
            </div>

            <div className="space-y-1">
              <Label>Teacher feedback</Label>
              <Textarea rows={5} value={result.teacherFeedback} onChange={(e) => updateResult('teacherFeedback', e.target.value)} data-testid="wm-teacher-feedback" />
            </div>

            {result.unclearRegions?.length > 0 && (
              <Card className="p-4 rounded-xl bg-sky-50 border-sky-200">
                <div className="font-semibold text-sky-900 mb-2">Items to check</div>
                <ul className="list-disc pl-5 text-sm text-sky-900 space-y-1">
                  {result.unclearRegions.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </Card>
            )}

            <div className="flex flex-wrap gap-3 pt-2">
              <Button variant="outline" onClick={reset} data-testid="wm-reset-btn">
                <RefreshCw className="w-4 h-4 mr-2" /> Mark another worksheet
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => downloadMarkingPdf(result, { studentName, level, worksheetType })}
                data-testid="wm-download-pdf-btn"
              >
                <FileDown className="w-4 h-4 mr-2" /> 3. Download PDF report
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              The PDF uses 16-point Times-style text. Verify all handwriting and marks before sharing with parents or students.
            </p>
          </Card>
        )}
      </main>
    </div>
  );
};

export default WorksheetMarkerPage;
